import { Injectable } from '@angular/core';
import { TasksModalContainerComponent } from '@app/hub/containers/tasks-modal-container/tasks-modal-container.component';
import { StateSpecificFlagsModel } from '@assets/metadata/stateSpecificFlags';
import { ProductType } from '@core/models/api/dsm-types';
import { QuoteRetrieveResponse } from '@core/models/api/response/quote-retrieve-response.model';
import { ErrorSanitizerService } from '@core/services/error-sanitizer.service';
import { FeatureFlagService } from '@core/services/feature-flag.service';
import { LoadingService } from '@core/services/loading.service';
import { MetadataService } from '@core/services/metadata.service';
import { ProductsService } from '@core/services/products.service';
import { SessionService } from '@core/services/session.service';
import {
  CoverageActions,
  DriverVehiclePrefillActions,
  ProductActions,
  RetrieveActions,
  TelematicsActions,
} from '@core/store/actions';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { ProductUtils } from '@shared/utils/product.util';
import { Observable, combineLatest, forkJoin, from, of } from 'rxjs';
import {
  catchError,
  concatWith,
  delay,
  delayWhen,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  tap,
  toArray,
  withLatestFrom,
} from 'rxjs/operators';
import {
  MinimalHydrateQuoteResponse,
  QuoteRetrieveAdapter,
} from '../../adapters/quote-retrieve.adapter';
import { NavigationActions } from '../actions';
import { setIsSpinOff } from '../entities/session/session.action';
import { updateAgency } from '../entities/agency/agency.action';
import { getDriverVehicleAssignment } from '../entities/driver-vehicle-assignment/driver-vehicle-assignment.actions';
import {
  navigatePostRetrieveComplete,
  navigatePostSpinOffRetrieveComplete,
} from '../entities/navigation/navigation.action';
import { replacePreBindDocumentsByProductType } from '../entities/pre-bind-documents/pre-bind-documents.action';
import { updateTasks } from '../entities/task/task.action';
import { excludeQuoteFromTelematics } from '../entities/telematics/telematics.action';
import { getUWReports } from '../entities/uw-reports/uw-reports.action';
import {
  AgencySelectors,
  DsmSelectors,
  TasksModalSelectors,
} from '../selectors';
import {
  allRetrievesComplete,
  hydrateQuote,
  hydrateQuoteError,
  hydrateQuoteSuccess,
  cancelHydrateQuote,
  retrieveQuoteError,
  retrieveQuoteSuccess,
  retrieveQuotes,
} from './retrieve.action';
import { getRetrievePendingCallCount } from './retrieve.selector';
import { filterOutNull } from '@shared/rxjs/filter-out-null.operator';
import { ScheduledCategoryActions } from '@core/store/actions/index';
import { DateUtils } from '@shared/utils/date.utils';
import { QuoteAdapter } from '@core/adapters/quote.adapter';
import { VersionedQuoteService } from '@entities/versioned-quote/versioned-quote.service';
import { getIncompleteDraftTasks } from '@entities/task/selectors/task.selector';
import { getCurrentCarrierComparison } from '@entities/product/product.selectors';

@Injectable()
export class RetrieveEffects {
  constructor(
    private actions$: Actions,
    private retrieveAdapter: QuoteRetrieveAdapter,
    private loadingService: LoadingService,
    private errorSanitizerService: ErrorSanitizerService,
    private featureFlagService: FeatureFlagService,
    private metadataService: MetadataService,
    private store: Store,
    private ngbModal: NgbModal,
    private productsService: ProductsService,
    private sessionService: SessionService,
    private quoteAdapter: QuoteAdapter,
    private versionedQuoteService: VersionedQuoteService
  ) {}

  versionedQuoteFormat = /^([^-]+)-([^-]+)$/;

  retrieveQuotes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(retrieveQuotes),
      map((a) => a.payload.quotes),
      // switchMap to create a closure to hold catchError
      switchMap((requestedQuotes) =>
        of(requestedQuotes).pipe(
          mergeMap((quotes) => {
            const responses: any[] = [];
            for (const { quoteId, productType, createDate } of quotes) {
              this.productsService.updateProductCreateDate(
                productType,
                createDate ||
                  DateUtils.formatDsmDateToOld(
                    DateUtils.getCurrentDateAsString()
                  )
              );
              const isVersionedQuote = quoteId.match(this.versionedQuoteFormat);
              if (isVersionedQuote) {
                const baseQuoteId = isVersionedQuote[1];
                const version = isVersionedQuote[2];
                responses.push(
                  this.quoteAdapter
                    .updateQuoteVersion(productType, quoteId, version, true)
                    .pipe(
                      concatWith(
                        this.retrieveAdapter.retrieve(baseQuoteId, productType)
                      )
                    )
                );
              } else {
                responses.push(
                  this.retrieveAdapter.retrieve(quoteId, productType)
                );
              }
            }
            const shouldSuppressSpinner = requestedQuotes.some(
              (q) => q.suppressSpinner
            );

            if (!shouldSuppressSpinner) {
              this.loadingService.beginLoading(
                'retrievequote',
                'Found it. Loading details'
              );
            }
            return forkJoin(responses as Observable<any>[]).pipe(
              takeUntil(this.actions$.pipe(ofType(cancelHydrateQuote)))
            );
          }),
          takeUntil(this.actions$.pipe(ofType(cancelHydrateQuote))),
          switchMap((responses) => {
            const state = responses.find(
              (resp) => !!resp.response?.policyAddress?.state
            )?.response.policyAddress.state;
            if (state) {
              this.sessionService.updateQuoteState(state);
            }
            return responses;
          }),
          toArray(),
          withLatestFrom(
            this.metadataService.getStateSpecificFlagsData(),
            this.productsService.getAllProducts(),
            this.sessionService.getIsComprater()
          ),
          mergeMap(
            ([responses, stateSpecificFlags, allProducts, isComprater]) => {
              const nextActions = [];
              const products: ProductType[] = [];
              const commonResponse = this.getCommonResponse(responses);
              (responses as any[]).forEach((retrieveResponse) =>
                products.push(retrieveResponse.productType)
              );
              const atLeastOneActiveProduct: boolean =
                ProductUtils.atLeastOneActive(
                  allProducts.filter((product) =>
                    products.includes(product.type)
                  )
                );
              this.assertValidStates(responses, stateSpecificFlags);

              nextActions.push(
                updateAgency({
                  payload: {
                    producerCode: commonResponse.producer.producerCode,
                    agent: {
                      agencyCode: commonResponse.producer.agencyCode,
                    },
                  },
                })
              );

              for (const retrieveResponse of responses) {
                const productModel = allProducts.find(
                  (p) => p.type === retrieveResponse.productType
                );
                if (!productModel?.isDsmActive) {
                  nextActions.push(
                    RetrieveActions.retrieveInactiveQuoteSuccess({
                      payload: retrieveResponse,
                    })
                  );
                } else {
                  if (retrieveResponse.response.offeredQuotes?.length > 0) {
                    retrieveResponse.response.offeredQuotes =
                      retrieveResponse.response.offeredQuotes.map((oc: any) => {
                        return {
                          ...oc,
                          coverages: (oc.coverages || []).map(
                            (coverage: any) => ({
                              ...coverage,
                              productId: retrieveResponse.productType,
                              version: oc.packageName,
                            })
                          ),
                        };
                      });
                  }
                  nextActions.push(
                    retrieveQuoteSuccess({
                      payload: retrieveResponse,
                      isComprater,
                    })
                  );
                }
              }
              if (atLeastOneActiveProduct) {
                nextActions.push(updateTasks());
                nextActions.push(
                  ...this.determineNavigationActions(requestedQuotes)
                );
                if (
                  products.includes('PersonalAuto') ||
                  products.includes('MSA')
                ) {
                  nextActions.push(
                    DriverVehiclePrefillActions.getCustomersInfo()
                  );
                }
              } else {
                this.loadingService.endLoading('retrievequote');
              }

              return from(nextActions);
            }
          ),
          catchError((error) => {
            this.loadingService.endLoading('retrievequote');
            return of(
              retrieveQuoteError({
                payload: this.errorSanitizerService.sanitizeError(error),
              })
            );
          })
        )
      )
    )
  );

  postRetrieveActions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RetrieveActions.retrieveQuoteSuccess),
      withLatestFrom(this.metadataService.getStateSpecificFlagsData()),
      mergeMap(([action, stateSpecificFlags]) => {
        const retrieveResponse = action.payload;
        const nextActions: Action[] = [];

        if ((retrieveResponse.response?.offeredQuotes?.length || 0) > 0) {
          nextActions.push(
            CoverageActions.runCoverageRules({
              payload: retrieveResponse.response.offeredQuotes[0].coverages,
            })
          );
        }

        if (retrieveResponse.productType === 'PersonalAuto') {
          if (
            this.featureFlagService.isFlagApplicable(
              stateSpecificFlags || ({} as StateSpecificFlagsModel),
              retrieveResponse.response.policyAddress.state as string,
              'driverVehicleAssignment',
              retrieveResponse.productType
            )
          ) {
            nextActions.push(
              getDriverVehicleAssignment({ product: 'PersonalAuto' })
            );
          }
          if (!retrieveResponse.response.telematicsEnrollments?.length) {
            nextActions.push(
              excludeQuoteFromTelematics({
                quoteId: retrieveResponse.response.quoteId,
              })
            );
          }
        }

        if (retrieveResponse.productType === 'MSA') {
          if (
            this.featureFlagService.isFlagApplicable(
              stateSpecificFlags || ({} as StateSpecificFlagsModel),
              retrieveResponse.response.policyAddress.state as string,
              'msaDriverVehicleAssignment',
              retrieveResponse.productType
            )
          ) {
            nextActions.push(getDriverVehicleAssignment({ product: 'MSA' }));
          }
        }

        if (retrieveResponse.productType === 'RV') {
          if (
            this.featureFlagService.isFlagApplicable(
              stateSpecificFlags || ({} as StateSpecificFlagsModel),
              retrieveResponse.response.policyAddress.state as string,
              'rvDriverVehicleAssignment',
              retrieveResponse.productType
            )
          ) {
            nextActions.push(getDriverVehicleAssignment({ product: 'RV' }));
          }
        }

        if (
          retrieveResponse.productType === 'PersonalAuto' &&
          this.featureFlagService.isFlagApplicable(
            stateSpecificFlags || ({} as StateSpecificFlagsModel),
            retrieveResponse.response.policyAddress.state as string,
            'preBindDocuments',
            retrieveResponse.productType
          ) &&
          !!retrieveResponse.response.documents?.length
        ) {
          nextActions.push(
            replacePreBindDocumentsByProductType({
              documents:
                retrieveResponse.response.documents.map((doc: any) => {
                  return {
                    ...doc,
                    productType: retrieveResponse.productType,
                  };
                }) || [],
              productType: retrieveResponse.productType,
            })
          );
        }

        if (
          ProductUtils.ReportsAndUWProducts.includes(
            retrieveResponse.productType
          )
        ) {
          nextActions.push(
            getUWReports({
              quoteId: retrieveResponse.response.quoteId,
              product: retrieveResponse.productType,
            })
          );
        }

        if (
          retrieveResponse.productType === 'Homeowner' ||
          retrieveResponse.productType === 'Condominium' ||
          retrieveResponse.productType === 'Tenant'
        ) {
          nextActions.push(
            ScheduledCategoryActions.loadScheduledCategories({
              payload: [retrieveResponse.productType],
            })
          );
        }

        return from(nextActions);
      })
    )
  );

  hydrateQuote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(hydrateQuote),
      switchMap(({ quoteId, producerCode }) =>
        this.retrieveAdapter.hydrateQuote(quoteId, producerCode).pipe(
          takeUntil(this.actions$.pipe(ofType(cancelHydrateQuote))),
          map((response) => hydrateQuoteSuccess({ payload: response })),
          catchError((error) => {
            return of(
              hydrateQuoteError({
                payload: this.errorSanitizerService.sanitizeError(error),
              })
            );
          })
        )
      )
    )
  );

  hydrateQuoteSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(hydrateQuoteSuccess),
      switchMap((res) =>
        of(res).pipe(
          delay(0),
          takeUntil(this.actions$.pipe(ofType(cancelHydrateQuote))),
          map((response) => {
            let quotes = response.payload
            .map((q) => ({
              quoteId: q.quoteId,
              productType: this.sanitizeIncomingProductType(q.productType),
              suppressSpinner: true,
            }))
            .filter((q) => q.quoteId && q.productType);
            quotes = this.filterToOneQuotePerProduct(quotes);
            return retrieveQuotes({
              payload: {
               quotes,
              },
            });
          })
        )
      )
    )
  );

  private filterToOneQuotePerProduct<T extends MinimalHydrateQuoteResponse>(
    quotes: T[]
  ): T[] {
    const unique: T[] = [];
    for (const quote of quotes) {
      const already = unique.find((u) => u.productType === quote.productType);
      if (already) {
        continue;
      }
      unique.push(quote);
    }
    return unique;
  }

  /* I'm not crazy about this, but the success case is already handled similarly (see retrieveQuote$)
   */
  dropSpinnerWhenHydrateFails$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(hydrateQuoteError, retrieveQuoteError),
        tap(() => this.loadingService.endLoading('hydrate-quote'))
      ),
    { dispatch: false }
  );

  dropSpinnerWhenPostRetrieveNavigationCompletes$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          navigatePostRetrieveComplete,
          navigatePostSpinOffRetrieveComplete
        ),
        tap(() => {
          this.loadingService.endLoading('hydrate-quote');
          this.loadingService.endLoading('retrievequote');
        })
      ),
    { dispatch: false }
  );

  signalCompletionOfMultipleRetrieveCalls$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        retrieveQuoteSuccess,
        RetrieveActions.retrieveInactiveQuoteSuccess,
        retrieveQuoteError
      ),
      withLatestFrom(this.store.select(getRetrievePendingCallCount)),
      filter(([action, pendingCallCount]) => !pendingCallCount),
      map(() => allRetrievesComplete())
    )
  );

  alertForTasksAfterRetrieve$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NavigationActions.navigatePostRetrieve),
        delayWhen(() =>
          this.store.select(DsmSelectors.getDsmCallCount).pipe(
            filter((callCount) => callCount === 0),
            take(1)
          )
        ),
        switchMap(() =>
          combineLatest([
            this.store
              .select(TasksModalSelectors.getTasksForPostRetrieveModal)
              .pipe(take(1)),
            this.store.select(getIncompleteDraftTasks).pipe(take(1)),
            this.store.select(getCurrentCarrierComparison).pipe(take(1)),
          ])
        ),
        map(([modalTasks, nonModalDraftTasks, currentCarrierComparison]) => {
          if (!modalTasks && nonModalDraftTasks.length > 0) {
            this.versionedQuoteService.setAwaitingVersionChoice(false);
          }
          if (!!modalTasks || currentCarrierComparison) {
            const modalRef = this.ngbModal.open(TasksModalContainerComponent, {
              centered: true,
              size: 'xl',
              backdrop: 'static',
              keyboard: false,
              ariaLabelledBy: 'tasksModalHeader',
            });
            modalRef.componentInstance.tasks = modalTasks;
          }
        })
      ),
    { dispatch: false }
  );

  updateProducersOnRetrieve$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RetrieveActions.retrieveQuoteSuccess),
      withLatestFrom(this.store.select(AgencySelectors.getProducerCodeSearch)),
      map(([_res, producerSearch]) => {
        const response = _res.payload.response;
        const productType = _res.payload.productType;
        const producer = producerSearch?.producers.find(
          (p) => response.producer.producerCode == p.producerCode
        );

        if (!producer) {
          return null;
        }
        return ProductActions.updateProducer({
          product: productType,
          producer: producer,
        });
      }),
      filterOutNull()
    )
  );
 
  private determineNavigationActions(requestedQuotes: any[]): Action[] {
    const isSpinOffRetrieve =
      Array.isArray(requestedQuotes) &&
      requestedQuotes.length > 0 &&
      requestedQuotes.every((q: any) => q.isSpinOff);
    const actions: Action[] = [];
    if (isSpinOffRetrieve) {
      actions.push(setIsSpinOff({ isSpinOff: true }));
    }
    actions.push(
      isSpinOffRetrieve
        ? NavigationActions.navigatePostSpinOffRetrieve()
        : NavigationActions.navigatePostRetrieve()
    );
    return actions;
  }

  private sanitizeIncomingProductType(input: string): ProductType {
    if (input === 'Homeowners') {
      return 'Homeowner';
    }
    // ok: PersonalAuto, Tenant
    // unknown: Condominium, powersports?
    return input as ProductType;
  }

  private assertValidStates(
    responses: QuoteRetrieveResponse[],
    stateSpecificFlags: StateSpecificFlagsModel
  ): void {
    for (const response of responses) {
      const state = response?.response?.policyAddress?.state;
      if (!state) {
        continue;
      }
      if (stateSpecificFlags.restrictAllProducts.states.includes(state)) {
        throw new Error(`Quote state not supported at this time (${state})`);
      }
    }
  }

  private getCommonResponse(responses: any[]): any {
    // untyped and dangerous
    return responses.find(
      (retrieveResponse) => !!retrieveResponse.response.producer
    ).response;
  }
}
